import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { SizeVariants } from '@/shared/forms/components/constants'
import Markdown from 'react-markdown'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'
import Link from 'next/link'
import { useFormContext } from 'react-hook-form'

type Props = {
  name: string
  label: string
  className?: string
  size?: keyof typeof SizeVariants
  disabled?: boolean
  optional?: boolean
  tooltip?: ReactNode
}

export default function RHFToggle({
  name,
  label,
  className,
  size = 'full',
  disabled,
  tooltip,
  optional = false,
}: Props) {
  const { formState, register, trigger } = useFormContext()
  const { isSubmitting, touchedFields, dirtyFields, errors } = formState
  const field = register(name)
  const isDisabled = disabled || isSubmitting
  const isTouched = touchedFields?.[name] || dirtyFields?.[name]
  const error = errors[name]

  return (
    <div className={clsx(SizeVariants[size as keyof typeof SizeVariants], className)}>
      <div className="flex items-center justify-start gap-x-3">
        <input
          {...field}
          onChange={async (e) => {
            await field.onChange(e)
            await trigger(name)
          }}
          type="checkbox"
          name={name}
          className={clsx(
            'h-4 w-4 border-gray-300 focus:ring-blue-600',
            isDisabled ? 'text-gray-300' : 'text-blue-600',
          )}
          disabled={isDisabled}
        />

        <label
          htmlFor={name}
          className={clsx(
            'block text-sm font-medium leading-6',
            isDisabled ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          <Markdown
            components={{
              a: (link) => (
                <Link
                  className="text-blue-600 hover:underline"
                  href={link.href!}
                  target="_blank"
                >
                  {link.children}
                </Link>
              ),
            }}
          >
            {label}
          </Markdown>
        </label>
        {tooltip && <SimpleTooltip content={tooltip} disabled={disabled} />}
        {optional && (
          <span className="text-sm leading-6 text-gray-500" id="email-optional">
            Optional
          </span>
        )}
      </div>
      {isTouched && error && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {`${error.message}`}
        </div>
      )}
    </div>
  )
}
